import { create } from 'zustand'

import type {
  CommitResult, ConvertResult, ItemRow, OrderRow, PartnerRow, PriceRow,
} from '@/pages/ConvertTab/types'

/**
 * 발주서 변환(ConvertTab)의 업로드/파싱/검토 상태를 담는 인메모리 스토어.
 *
 * 왜 스토어인가: 코드매칭·부서매칭 탭으로 이동하면 ConvertTab 이 언마운트되어
 * useState 로 들고 있던 업로드/변환 결과가 사라진다. 스토어로 올려두면 탭을
 * 오가도 "처음 발주서 업로드했던 상태"가 그대로 유지된다.
 *
 * localStorage 로 persist 하지 않는다 — File 객체가 직렬화 안 되고, 페이지를
 * 새로고침하면 초기화되는 편이 자연스럽다(세션 내 탭 이동 동안만 유지).
 */

type Up<T> = T | ((prev: T) => T)
const res = <T,>(u: Up<T>, prev: T): T =>
  typeof u === 'function' ? (u as (p: T) => T)(prev) : u

interface ConvertDraft {
  groupId: number | null
  groupLabel: string
  files: File[]
  mergeSameDay: boolean
  result: ConvertResult | null
  partnerRows: PartnerRow[]
  itemRows: ItemRow[]
  priceRows: PriceRow[]
  orderRows: OrderRow[]
  committed: CommitResult | null
  expanded: Set<number>

  setGroupId: (u: Up<number | null>) => void
  setGroupLabel: (u: Up<string>) => void
  setFiles: (u: Up<File[]>) => void
  setMergeSameDay: (u: Up<boolean>) => void
  setResult: (u: Up<ConvertResult | null>) => void
  setPartnerRows: (u: Up<PartnerRow[]>) => void
  setItemRows: (u: Up<ItemRow[]>) => void
  setPriceRows: (u: Up<PriceRow[]>) => void
  setOrderRows: (u: Up<OrderRow[]>) => void
  setCommitted: (u: Up<CommitResult | null>) => void
  setExpanded: (u: Up<Set<number>>) => void
  reset: () => void
}

const blank = () => ({
  groupId: null as number | null,
  groupLabel: '',
  files: [] as File[],
  mergeSameDay: false,
  result: null as ConvertResult | null,
  partnerRows: [] as PartnerRow[],
  itemRows: [] as ItemRow[],
  priceRows: [] as PriceRow[],
  orderRows: [] as OrderRow[],
  committed: null as CommitResult | null,
  expanded: new Set<number>(),
})

export const useConvertDraft = create<ConvertDraft>((set) => ({
  ...blank(),
  setGroupId: (u) => set((s) => ({ groupId: res(u, s.groupId) })),
  setGroupLabel: (u) => set((s) => ({ groupLabel: res(u, s.groupLabel) })),
  setFiles: (u) => set((s) => ({ files: res(u, s.files) })),
  setMergeSameDay: (u) => set((s) => ({ mergeSameDay: res(u, s.mergeSameDay) })),
  setResult: (u) => set((s) => ({ result: res(u, s.result) })),
  setPartnerRows: (u) => set((s) => ({ partnerRows: res(u, s.partnerRows) })),
  setItemRows: (u) => set((s) => ({ itemRows: res(u, s.itemRows) })),
  setPriceRows: (u) => set((s) => ({ priceRows: res(u, s.priceRows) })),
  setOrderRows: (u) => set((s) => ({ orderRows: res(u, s.orderRows) })),
  setCommitted: (u) => set((s) => ({ committed: res(u, s.committed) })),
  setExpanded: (u) => set((s) => ({ expanded: res(u, s.expanded) })),
  reset: () => set(blank()),
}))
